"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader, CircleCheck } from "lucide-react";
export default function TheInnerCircle() {
  return <section data-section-id="125939" id="free-strategy-assessment" className="bg-background text-foreground py-24 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="border-none rounded-3xl p-8 md:p-12 bg-primary text-primary-foreground shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="relative z-10 mb-10 text-center md:text-left">
            <h2 className="text-4xl md:text-5xl font-bold font-serif mb-4 text-primary-foreground">
              Start Your Journey
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/90 font-sans max-w-2xl">
              Receive your complimentary Strategy Assessment. We'll identify your growth stage, uncover bottlenecks, and outline a clear roadmap to scale your business.
            </p>
          </div>

          <FormProvider formKey="free_assessment_form_home" formId="free_assessment_form_home" sectionName="free-strategy-assessment" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          goal: "",
          agree_updates: false
        }} validationRules={{
          full_name: {
            required: "Please enter your full name"
          },
          email: {
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          },
          agree_updates: {
            required: "You must agree to the privacy promise to continue"
          }
        }} submitText="Get My Assessment" successMessage="Your assessment is on its way. Check your inbox for the next steps.">
            <InnerCircleForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function InnerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-12 flex flex-col items-center justify-center text-center animate-in fade-in duration-500">
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold font-serif mb-2 text-primary-foreground">Assessment Requested!</h3>
        <p className="text-primary-foreground/80 text-lg max-w-md font-sans">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-primary-foreground font-medium font-sans">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Alex Rivera" {...field} className="h-12 text-base bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-secondary rounded-lg" />
            </FormControl>
            <FormDescription className="text-xs text-primary-foreground/70 font-sans">
              Tell us who you are so we can personalize your roadmap.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
            <FormLabel className="text-primary-foreground font-medium font-sans">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@creativefirm.com" {...field} className="h-12 text-base bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-secondary rounded-lg" />
            </FormControl>
            <FormDescription className="text-xs text-primary-foreground/70 font-sans">
              Your strategy assessment will be delivered here.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="md:col-span-2">
            <FormLabel className="text-primary-foreground font-medium font-sans">Phone Number (optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="h-12 text-base bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-secondary rounded-lg" />
            </FormControl>
            <FormDescription className="text-xs text-primary-foreground/70 font-sans">
              Include your number if you prefer a text-based follow-up.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="goal" render={({
        field
      }) => <FormItem className="md:col-span-2">
            <FormLabel className="text-primary-foreground font-medium font-sans">Primary Business Goal</FormLabel>
            <FormControl>
              <Textarea placeholder="e.g., Scaling to $10k months, streamlining operations, or launching a VIP offer..." {...field} className="min-h-[120px] bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-secondary/50 rounded-lg resize-y font-sans" />
            </FormControl>
            <FormDescription className="text-xs text-primary-foreground/70 font-sans">
              Briefly describe what you want to achieve in the next 6 months.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="agree_updates" render={({
        field
      }) => <FormItem className="md:col-span-2 flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-white/10 bg-white/5 p-4">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-5 shrink-0 rounded border-white/30 bg-white/10 text-secondary focus:ring-secondary focus:ring-offset-primary" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary-foreground font-medium cursor-pointer font-sans">
                Privacy Promise: I agree to receive strategic updates and offers.
              </FormLabel>
              <FormDescription className="text-xs text-primary-foreground/70 font-sans">
                We value your inbox as much as you do. Unsubscribe anytime.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-lg text-white">
          <p className="font-sans">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto text-lg h-14 px-8 font-bold tracking-tight bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg rounded-lg transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Preparing Roadmap...
          </> : 'Get My Assessment'}
      </Button>
    </Form>;
}